import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';

import { OfferSource } from '@cds';
import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { EventModel, EventOption, EventOptionGroup, OptionTypes, VerticalDesignOptions } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { Group } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { at, isEmpty } from 'lodash-es';

import { EpcotConfigService } from '../../../common/epcot-config.service';
import { OptionComponent } from './option.component';

@Component({
    selector: 'ms-option-group',
    templateUrl: 'option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FontResizerComponent, IconCustomComponent, OptionComponent, AutomationModule],
})
export class OptionGroupComponent {
    @Input() optionGroup: EventOptionGroup;
    @Input() event: EventModel;
    @Input() group: Group;
    @Input() tracking: TrackingSource;
    @Input() decorateAttribute?: boolean;
    @Input() showOptionname: boolean;
    @Input() betDetails = '';
    @Input() verticalDesignOptions?: VerticalDesignOptions;
    @Input() showCenterOptionName = false;

    showSuspendedSelection: boolean;
    readonly isEpcotEnabled: boolean;

    @HostBinding('class.grid-option-group') main = true;
    @HostBinding('class.grid-group') secondary = true;

    @HostBinding('class.offline')
    get offline(): boolean {
        return this.showSuspendedSelection ? !this.optionGroup?.options?.length : !this.optionGroup?.online || !this.optionGroup?.options?.length;
    }

    @HostBinding('class.suspended-lock-box')
    get suspendedLockBox(): boolean {
        return this.showSuspendedSelection && !this.optionGroup?.options?.length;
    }

    @HostBinding('class.two-column')
    get threeColumn(): boolean {
        return (
            !this.optionGroup ||
            !this.optionGroup.online ||
            !this.optionGroup.options ||
            (this.optionGroup.options.length === 2 && !this.optionGroup.categoryAttribute) ||
            !!this.verticalDesignOptions?.isVerticalDesignEnabled
        );
    }

    @HostBinding('class.vertical')
    get verticalDesign(): boolean {
        return !!this.verticalDesignOptions?.isVerticalDesignEnabled;
    }

    constructor(
        private eventDetailsConfig: EventDetailsConfig,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.showSuspendedSelection = this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled;
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    get options(): EventOption[] {
        if (!isEmpty(this.optionGroup?.options)) {
            const optionsToShow = this.event.offerSource === OfferSource.V2 ? this.group?.optionsToShow?.v2 : this.group?.optionsToShow?.v1;

            if (!isEmpty(optionsToShow)) {
                const optionsToShowZeroBased = optionsToShow!.map((o) => --o);

                return at(this.optionGroup.options, optionsToShowZeroBased).filter((o) => !!o);
            }
        }

        return this.optionGroup.options;
    }

    shouldHighlight(option: EventOption): boolean {
        return this.event.fixtureForm ? this.event.isBestPerformingOption(option, this.optionGroup) : false;
    }

    getAutomationAttributes(option: EventOption, optionGroup: EventOptionGroup): { optionId: number; templateId: string; online: boolean } {
        return {
            optionId: option.id,
            templateId: optionGroup.templateId,
            online: option.online,
        };
    }

    showOptionNameByOptionTypes(option: EventOption): boolean {
        return this.showCenterOptionName && option.optionTypes?.includes(OptionTypes.NotExists)!;
    }
}
